import usePageState from '@/state/pageState';
import { getPublicBaseUrl } from '@/utils/publicSite';

type PublicSeoMeta = {
  canonical?: string;
  description?: string;
  image?: string;
  ogType?: string;
  robots?: string;
  structuredData?: Record<string, unknown>;
  title?: string;
  twitterCard?: string;
};

const ensureMetaTag = (attribute: 'name' | 'property', key: string) => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  return tag;
};

const setMetaTag = (attribute: 'name' | 'property', key: string, value?: string) => {
  const existing = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;

  if (!value?.length) {
    existing?.remove();
    return;
  }

  const tag = existing ?? ensureMetaTag(attribute, key);
  tag.setAttribute('content', value);
};

const setCanonicalLink = (href?: string) => {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!href?.length) {
    link?.remove();
    return;
  }

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
};

const STRUCTURED_DATA_SCRIPT_ID = 'bc-public-seo-structured-data';

const setStructuredData = (data?: Record<string, unknown>) => {
  const existing = document.head.querySelector(
    `script[data-seo-id="${STRUCTURED_DATA_SCRIPT_ID}"]`
  ) as HTMLScriptElement | null;

  if (!data) {
    existing?.remove();
    return;
  }

  const script = existing ?? document.createElement('script');

  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-seo-id', STRUCTURED_DATA_SCRIPT_ID);
  script.textContent = JSON.stringify(data);

  if (!existing) {
    document.head.appendChild(script);
  }
};

export const toAbsolutePublicUrl = (pathOrUrl?: string) => {
  if (!pathOrUrl?.length) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${getPublicBaseUrl()}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
};

export const clearPublicSeo = () => {
  setCanonicalLink(undefined);
  setMetaTag('property', 'og:title', undefined);
  setMetaTag('property', 'og:description', undefined);
  setMetaTag('property', 'og:type', undefined);
  setMetaTag('property', 'og:url', undefined);
  setMetaTag('property', 'og:image', undefined);
  setMetaTag('name', 'twitter:card', undefined);
  setMetaTag('name', 'twitter:title', undefined);
  setMetaTag('name', 'twitter:description', undefined);
  setMetaTag('name', 'twitter:image', undefined);
  setStructuredData(undefined);
};

export const updatePublicSeo = ({
  title,
  description,
  robots,
  canonical,
  image,
  structuredData,
  ogType = 'website',
  twitterCard = 'summary_large_image'
}: PublicSeoMeta) => {
  const { title: pageTitle } = usePageState();

  if (title?.length) {
    pageTitle.value = title;
  }

  setMetaTag('name', 'description', description);
  setMetaTag('name', 'robots', robots);
  setCanonicalLink(canonical);

  setMetaTag('property', 'og:title', title);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'og:type', ogType);
  setMetaTag('property', 'og:url', canonical);
  setMetaTag('property', 'og:image', image);

  setMetaTag('name', 'twitter:card', twitterCard);
  setMetaTag('name', 'twitter:title', title);
  setMetaTag('name', 'twitter:description', description);
  setMetaTag('name', 'twitter:image', image);
  setStructuredData(structuredData);
};
